/*
 * SearchAllPartsForm
 */

import * as React from 'react';

import { TFunction } from 'i18next';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import { Button, Col, Row } from 'react-bootstrap';
import { FieldConfig, FormArray, FormGenerator, FormGroup, GroupProps, Validators } from 'react-reactive-form';
import { toastr } from 'react-redux-toastr';

import { searchPartsForAsyncSelect } from '../../actions/partsActions';
import { getBundleOptions } from '../../components/common/ShoppingCart/ShoppingCartSubSubItem';
import { constants } from '../../constants/constants';
import { Ipart } from '../../models';
import { partTypeEnum } from '../../models-enums';
import { IgenericFormValues } from '../../modelsForms';
import { initialPart } from '../../reducers/initialState';
import { FormUtil } from '../common/FormUtil';

interface Iprops {
    loading: boolean;
    colorButton: string;
    t: TFunction;
    closeModal?: () => void;
    onSubmit: (part: Ipart) => void;
    shouldShowQuantity?: boolean;
    shouldShowQuote?: boolean;
    partType: partTypeEnum;
}

interface Istate {
    fieldConfig: FieldConfig;
    parts: Ipart[];
}

export class SearchAllPartsForm extends React.Component<Iprops, Istate> {
    private formGroup: FormGroup | any;
    private subscription: any;
    static defaultProps = {
        shouldShowQuantity: false,
        shouldShowQuote: false
    };
    constructor(props: Iprops) {
        super(props);
        this.state = {
            fieldConfig: this.buildFieldConfig(),
            parts: []
        };
    }
    componentDidMount() {
        this.setState({
            fieldConfig: this.buildFieldConfig(this.itemToFormValues())
        });
    }

    componentWillUnmount() {
        if (this.subscription) {
            this.subscription.unsubscribe();
        }
    }

    /*
     * itemToFormValues - take the selectedItem and convert it to formValues
     */
    itemToFormValues = (): IgenericFormValues<Ipart> => {
        return {};
    };

    /*
     * formValuesToItem - convert the formValues to the shape of the selectedItem
     */
    formValuesToItem = (): Ipart => {
        const processedFormValues = FormUtil.getValues(this.formGroup.value);
        const foundPart =
            (find(this.state.parts, {
                id: processedFormValues.materialID
            }) as Ipart) || initialPart;
        return {
            ...initialPart,
            ...foundPart,
            ...processedFormValues
        };
    };

    loadOptions = debounce((searchTerm, callback) => {
        searchPartsForAsyncSelect(searchTerm, this.props.partType).then(result => {
            this.setState({ parts: result.parts });
            callback(result.partOptions);
        });
    }, 500);

    buildFieldConfig = (defaultValues = this.itemToFormValues()) => {
        const disabled = false;

        // Field config to configure form
        let fieldConfigControls = {
            materialID: {
                options: {
                    validators: [Validators.required]
                },
                render: FormUtil.AsyncSelect,
                meta: {
                    label:
                        this.props.partType === partTypeEnum.default
                            ? 'searchAllPartsModal.part'
                            : 'searchAllPartsModal.labor',
                    colWidth: 8,
                    loadOptions: this.loadOptions,
                    name: 'part',
                    required: true,
                    isMulti: false
                },
                formState: {
                    value: null,
                    disabled
                }
            },
            quantity: {
                options: {
                    validators: [Validators.required]
                },
                render: FormUtil.NumericInput,
                meta: {
                    label: 'searchAllPartsModal.quantity',
                    colWidth: 3,
                    name: 'quantity',
                    required: true
                },
                formState: {
                    value: 1,
                    disabled
                }
            },
            bundle: {
                options: {
                    validators: [Validators.required]
                },
                render: FormUtil.Select,
                meta: {
                    options: getBundleOptions(),
                    label: 'partCartModal.bundle',
                    colWidth: 6,
                    name: 'bundle',
                    required: true
                },
                formState: {
                    value: 1,
                    disabled
                }
            }
        } as { [key: string]: GroupProps };

        if (this.props.shouldShowQuantity === false) {
            delete fieldConfigControls.quantity;
        }

        if (this.props.shouldShowQuote === false) {
            delete fieldConfigControls.bundle;
        }

        const fieldConfig = {
            controls: { ...fieldConfigControls }
        };

        return FormUtil.translateForm(fieldConfig, this.props.t);
    };

    handleSubmit = (e: React.MouseEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (this.formGroup.status === 'INVALID') {
            this.formGroup.markAsSubmitted();
            toastr.error('Please check invalid inputs', '', constants.toastrError);
            return;
        }

        this.props.onSubmit(this.formValuesToItem());

        if (this.props.closeModal) {
            this.props.closeModal();
        }
    };

    setForm = (form: FormGroup | FormArray) => {
        this.formGroup = form;
        this.formGroup.meta = {
            loading: this.props.loading
        };
    };

    render() {
        const { t, partType, closeModal } = this.props;

        const formClassName = `clearfix job-form beacon-form ${this.props.colorButton}`;
        if (partType === partTypeEnum.default && closeModal) {
            return (
                <div className={formClassName}>
                    <Col xs={12}>
                        <Row>
                            <FormGenerator onMount={this.setForm} fieldConfig={this.state.fieldConfig} />
                        </Row>
                    </Col>

                    <div className="form-buttons labor">
                        <Button
                            variant="default"
                            type="button"
                            className="cancel-button"
                            onClick={() => {
                                closeModal();
                            }}
                        >
                            {String(t('cancel'))}
                        </Button>
                        <Button variant={this.props.colorButton} type="submit" onClick={this.handleSubmit}>
                            {String(t('searchAllPartsModal.addButton'))}
                        </Button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className={formClassName}>
                    <Row>
                        <FormGenerator onMount={this.setForm} fieldConfig={this.state.fieldConfig} />

                        <Button
                            variant={this.props.colorButton}
                            type="submit"
                            onClick={this.handleSubmit}
                            className="search-all-parts-button"
                        >
                            {String(t('searchAllPartsModal.addButton'))}
                        </Button>
                    </Row>
                </div>
            );
        }
    }
}
